/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../../components/ui/sheet";
import { BookOpen, Download, X, Info, AlertTriangle } from "lucide-react";
import { downloadUserGuide } from "../lib/userGuide";

// In-app preview of the same content found in the downloadable PDF user
// guide.  Written in plain Australian English. Two-pane layout: sticky
// table of contents on the left, scrollable body on the right.
// Drawer slides in from the right edge.

const NAV = [
  { id: "intro", label: "What this guide covers", keywords: ["overview", "intro"] },
  { id: "ch1", label: "1. Getting started", keywords: ["header", "top bar", "currency", "tags", "save", "load", "tabs", "navigation", "language"] },
  { id: "ch2", label: "2. Building the scope", keywords: ["sbs", "system breakdown", "data modules", "matrix", "illustrations", "dml", "sns", "brex", "br", "dmc", "decision points"] },
  { id: "ch3", label: "3. Planning the work", keywords: ["roles", "rates", "rate card", "approval", "draft", "pending", "approved", "rejected", "resourcing", "fte", "schedule", "phases"] },
  { id: "ch4", label: "4. Preparing the bid", keywords: ["risk", "register", "assumptions", "dependencies", "bid pricing", "margin", "win probability", "summary", "readiness", "scorecard", "seed pack", "bid pack", "share"] },
  { id: "ch5", label: "5. Library", keywords: ["compare", "fx", "fallback", "portfolio", "saved projects", "history", "revisions"] },
  { id: "glossary", label: "Glossary", keywords: ["sbs", "sns", "dm", "dmc", "dml", "icn", "pmc", "brex", "dp", "fte", "blended", "overhead", "contingency", "seed", "bid pack"] },
  { id: "faq", label: "FAQ", keywords: ["faq", "delete", "store", "data", "fx", "rates"] },
];

function H2({ id, children }) {
  return (
    <h2 id={id} className="font-heading text-2xl font-bold text-purple-800 tracking-tight scroll-mt-4 border-b border-purple-100 pb-2 mt-8 mb-3 first:mt-0">
      {children}
    </h2>
  );
}
function H3({ children }) {
  return <h3 className="font-heading text-lg font-bold text-gray-900 mt-5 mb-2">{children}</h3>;
}
function H4({ children }) {
  return <h4 className="text-sm font-bold uppercase tracking-wider text-purple-800 mt-4 mb-1.5">{children}</h4>;
}
function P({ children }) {
  return <p className="text-sm text-gray-800 leading-relaxed mb-3">{children}</p>;
}
function UL({ children }) {
  return <ul className="space-y-1.5 mb-3 text-sm text-gray-800 leading-relaxed list-disc pl-5">{children}</ul>;
}
function Callout({ kind = "tip", label, children }) {
  const palette = {
    tip: { border: "border-l-purple-700", bg: "bg-yellow-50", text: "text-purple-800", Icon: Info },
    warn: { border: "border-l-red-600", bg: "bg-red-50", text: "text-red-600", Icon: AlertTriangle },
    note: { border: "border-l-gray-500", bg: "bg-gray-100", text: "text-gray-700", Icon: Info },
  }[kind];
  const Icon = palette.Icon;
  return (
    <div className={`border-l-4 ${palette.border} ${palette.bg} px-3 py-2 my-3 text-sm`}>
      <div className={`flex items-center gap-1.5 mb-1 ${palette.text} text-[10px] font-bold uppercase tracking-wider`}>
        <Icon size={12} /> {label}
      </div>
      <div className="text-gray-800 leading-relaxed">{children}</div>
    </div>
  );
}
function ActionTable({ rows }) {
  return (
    <div className="border border-gray-300 mb-3 text-sm">
      <div className="grid grid-cols-[200px_1fr] bg-gray-800 text-white text-[11px] font-bold uppercase tracking-wider">
        <div className="px-3 py-1.5">Where to click</div>
        <div className="px-3 py-1.5 border-l border-gray-600">What it does</div>
      </div>
      {rows.map(([a, b], i) => (
        <div key={i} className={`grid grid-cols-[200px_1fr] border-t border-gray-200 ${i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}`}>
          <div className="px-3 py-2 font-bold text-gray-800">{a}</div>
          <div className="px-3 py-2 text-gray-700 border-l border-gray-200">{b}</div>
        </div>
      ))}
    </div>
  );
}
function Screenshot({ src, caption }) {
  return (
    <figure className="my-3 border border-gray-300 bg-[#F9FAFB] p-2">
      <img src={src} alt={caption} loading="lazy" className="w-full block border border-gray-200" />
      <figcaption className="text-[11px] text-gray-500 italic mt-1.5">
        Screenshot: {caption}
      </figcaption>
    </figure>
  );
}

export default function UserGuidePanel({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [activeAnchor, setActiveAnchor] = useState("intro");
  const [search, setSearch] = useState("");

  // Filter the TOC by label or keyword match (case-insensitive).  Empty
  // query returns the full TOC unchanged.
  const visibleNav = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return NAV;
    return NAV.filter((n) =>
      n.label.toLowerCase().includes(q) ||
      (n.keywords || []).some((k) => k.toLowerCase().includes(q))
    );
  })();

  const jumpTo = (id) => {
    setActiveAnchor(id);
    const el = document.getElementById(`guide-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Listen for "open-user-guide" events from anywhere in the app so the
  // tiny HelpIconButton can open this drawer to a specific anchor without
  // needing prop drilling or a dedicated context.
  useEffect(() => {
    const handler = (e) => {
      const anchor = (e && e.detail && e.detail.anchor) || "intro";
      onOpenChange(true);
      // The drawer's content node is mounted asynchronously by Radix; wait
      // a frame before trying to scroll to the anchor.
      setTimeout(() => jumpTo(anchor), 80);
    };
    window.addEventListener("open-user-guide", handler);
    return () => window.removeEventListener("open-user-guide", handler);
  }, [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-4xl p-0 overflow-hidden flex flex-col"
        data-testid="user-guide-panel"
      >
        <SheetHeader className="border-b border-purple-200 px-5 py-3 bg-purple-800 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-200">
                Help · Plain Australian English
              </p>
              <SheetTitle className="font-heading text-2xl font-bold text-white tracking-tight flex items-center gap-2 mt-0.5">
                <BookOpen size={20} /> User Guide
              </SheetTitle>
              <SheetDescription className="text-purple-200 text-xs mt-0.5">
                A plain-language walkthrough for bid managers, S1000D specialists and technical authors.
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => downloadUserGuide()}
                data-testid="user-guide-panel-pdf-btn"
                className="bg-white text-purple-800 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1.5"
                title={t("actions.guide")}
              >
                <Download size={12} /> PDF
              </button>
              <button
                onClick={() => onOpenChange(false)}
                data-testid="user-guide-panel-close"
                className="text-white hover:bg-white/10 p-1.5"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </SheetHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* TOC */}
          <nav className="w-56 shrink-0 border-r border-gray-300 bg-[#F9FAFB] overflow-y-auto py-3" data-testid="user-guide-toc">
            <div className="px-4 mb-2">
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearch(v);
                  const q = v.trim().toLowerCase();
                  if (q) {
                    const first = NAV.find((n) =>
                      n.label.toLowerCase().includes(q) ||
                      (n.keywords || []).some((k) => k.toLowerCase().includes(q))
                    );
                    if (first) jumpTo(first.id);
                  }
                }}
                placeholder="Search the guide…"
                data-testid="user-guide-search"
                className="w-full border border-gray-300 rounded-none px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 bg-white"
              />
              {search && (
                <p className="text-[10px] text-gray-500 font-mono mt-1"
                  data-testid="user-guide-search-stats">
                  {visibleNav.length} {visibleNav.length === 1 ? "match" : "matches"}
                </p>
              )}
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 px-4 mb-1.5">
              On this page
            </p>
            {visibleNav.length === 0 && (
              <p className="px-4 text-[11px] text-gray-500 italic"
                data-testid="user-guide-no-results">
                Nothing matches "{search}". Clear the box to see the full guide.
              </p>
            )}
            {visibleNav.map((n) => (
              <button
                key={n.id}
                onClick={() => jumpTo(n.id)}
                data-testid={`user-guide-toc-${n.id}`}
                className={
                  "w-full text-left px-4 py-1.5 text-xs font-bold border-l-2 transition-colors " +
                  (activeAnchor === n.id
                    ? "bg-white border-l-purple-700 text-purple-800"
                    : "border-l-transparent text-gray-600 hover:bg-white hover:text-gray-900")
                }
              >
                {n.label}
              </button>
            ))}
          </nav>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="user-guide-body">
            <section id="guide-intro">
              <H2 id="guide-intro">What this guide covers</H2>
              <P>
                This guide explains how to use the S1000D Cost Estimator from your first
                blank project through to handing over a complete bid pack. Each tab in
                the app has its own chapter so you can skip straight to the part you need.
              </P>
              <UL>
                <li><strong>Getting started:</strong> opening the app, navigating the tabs and saving your work.</li>
                <li><strong>Building the scope:</strong> System Breakdown Structure, Data Modules, Illustrations and the DML.</li>
                <li><strong>Planning the work:</strong> roles, rate cards, resourcing and schedule.</li>
                <li><strong>Preparing the bid:</strong> risks, pricing, readiness, seed packs and bid packs.</li>
                <li><strong>Library:</strong> comparing estimates, portfolio dashboard and saved projects.</li>
              </UL>
              <Callout kind="tip" label="Plain-language note">
                Words like "authorise", "organise" and "centre" are spelt the Australian way throughout this guide.
                The app itself shows whichever spelling matches the language you select in the top-right corner.
              </Callout>
            </section>

            <section id="guide-ch1">
              <H2 id="guide-ch1">1. Getting started</H2>
              <H3>Opening the estimator</H3>
              <P>
                Open the app in any modern web browser. You do not need to sign in — every estimate is saved
                on the server by name. The first screen shows an empty project on the Data Modules tab.
              </P>
              <H3>Top bar at a glance</H3>
              <Screenshot src="/guide-assets/header.jpg" caption="the application header — project name, currency selector, FX status pill, language switcher and action buttons." />
              <ActionTable rows={[
                ["Project name", "Give your estimate a clear name. This is the title used in CSV, Excel and PDF exports."],
                ["Currency", "Choose USD, EUR, GBP, AUD or CAD. Every dollar figure updates straight away."],
                ["Tags", "Type a tag (for example 'defence' or 'rotary') and press Enter. Tags help you filter saved projects later."],
                ["Total project cost", "A live running total including overhead, contingency, and BREX cost when merged."],
                ["Save / New / Duplicate / Share", "Save writes to the database. New clears the workspace. Duplicate makes a copy. Share copies a read-only link."],
                ["CSV / XLSX / PDF / Guide", "Export the current estimate or download this user guide."],
                ["Language switcher", "English, French, German, Spanish or Italian — also localises numbers and dates."],
              ]} />
              <H3>Tab navigation</H3>
              <P>
                Tabs are organised into four groups across the top: Scope, Plan, Bid and Library.
                Click a group to switch to its first sub-tab, or click a sub-tab directly.
              </P>
              <UL>
                <li><strong>Scope</strong> — System Breakdown, Data Modules, Illustrations, DML / SNS, BR / BREX.</li>
                <li><strong>Plan</strong> — Roles &amp; Rates, Rate Cards, Resourcing, Schedule.</li>
                <li><strong>Bid</strong> — Risk, Bid Pricing, Summary, Readiness.</li>
                <li><strong>Library</strong> — Compare, Portfolio, Saved Projects.</li>
              </UL>
              <H3>Save, load and revisions</H3>
              <P>
                Click Save to write the current estimate. The first save creates a new record; subsequent
                saves overwrite it and keep an unlimited revision history. Open Library → Saved Projects
                to load, duplicate, delete or restore any earlier revision.
              </P>
              <Callout kind="tip" label="Tip">
                Save early and save often. Every Save snapshots the previous state into the revision log
                so you can roll back to any point in time without losing the work you've done since.
              </Callout>
            </section>

            <section id="guide-ch2">
              <H2 id="guide-ch2">2. Building the scope</H2>

              <H3>2.1 System Breakdown Structure (SBS)</H3>
              <P>
                The SBS tab is the recommended starting point. You describe the product as a tree of
                Systems, Sub-Systems and Sub-Subsystems, then tell the estimator how many General items
                and Repairable items live at each level.
              </P>
              <Screenshot src="/guide-assets/sbs.jpg" caption="the System Breakdown Structure tab — tree editor on top, roll-up cards in the middle, DMC inventory preview at the bottom." />
              <H4>Step by step</H4>
              <UL>
                <li>Click <strong>Add System</strong> to create a top-level entry — name it something like "Hydraulics".</li>
                <li>Use the <strong>+ Add Sub-System</strong> button on a system row to nest children. Repeat for Sub-Subsystems.</li>
                <li>Fill in the <strong>General qty</strong> and <strong>Repairable qty</strong> columns for every node that has items.</li>
                <li>Pick the General DM type and Repairable DM type at the top of the tab (defaults: 040 Descriptive, 520 Procedural).</li>
                <li>Set the hours-per-item for Simple, Intermediate and Complex levels.</li>
              </UL>
              <H4>Mapping rule</H4>
              <P>
                Systems become Simple, Sub-Systems become Intermediate, and Sub-Subsystems become Complex.
                General-item quantities feed the General DM type row; Repairable-item quantities feed the Repairable DM type row.
              </P>
              <H4>Importing from a spreadsheet</H4>
              <P>
                Click <strong>Template</strong> to download a starter workbook with the right column headings:
                System, Sub-System, Sub-Subsystem, General, Repairable. Blank Sub or Sub-Sub cells mean the
                row applies to its parent level. Choose Replace or Merge before uploading.
              </P>
              <H4>Pushing into the DM matrix</H4>
              <P>
                Click <strong>Sync to DM Matrix (Replace)</strong> to overwrite the quantities for the chosen DM types
                across the 3 complexity levels. Merge adds to existing quantities instead.
              </P>
              <H4>DMC inventory preview</H4>
              <P>
                Once your tree has items, the DMC Inventory section lists ready-made DMCs (for example
                <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 mx-0.5">PROJECT-A-21-10-00-00-0001-040A-D</code>).
                Click <strong>Download DMC inventory (CSV)</strong> to grab them, or include them automatically in the CSDB Seed Pack export.
              </P>
              <Callout kind="note" label="Note">
                If the active SNS preset doesn't have enough depth to cover your SBS tree, the missing codes
                fall back to "00". Pick an SNS preset that matches your product family for the cleanest DMCs.
              </Callout>

              <H3>2.2 Data Modules</H3>
              <P>
                The Data Modules tab is the heart of the estimate. Every S1000D data module type has a
                quantity and hours-per-unit at three complexity bands (Simple, Intermediate, Complex).
                Row totals, column totals and the grand total all update as you type.
              </P>
              <Screenshot src="/guide-assets/datamodules.jpg" caption="the Data Modules matrix — every S1000D DM type with editable qty + hours-per-unit at 3 complexity bands." />
              <UL>
                <li>Quantities default to zero — fill only the rows that apply to your contract.</li>
                <li>Hours-per-unit start at the indicative 6 / 8 / 12 figures but every cell is editable.</li>
                <li>Use <strong>Import</strong> to bring back a CSV / XLSX exported earlier. Replace or Merge as needed.</li>
                <li>Leading zeros in DM codes (such as 040) are preserved when you import.</li>
              </UL>

              <H3>2.3 Illustrations</H3>
              <P>
                Illustrations have their own table at three complexity bands. Set the quantity and the
                hours per illustration for Simple line drawings, Intermediate exploded views and Complex
                3D / cut-away renders.
              </P>

              <H3>2.4 DML / SNS</H3>
              <P>
                The DML (Data Management List) tab tracks the SNS hierarchy and lets you tune the per-DMC,
                per-ICN and per-PMC processing time. Pick a maintained preset (Generic, Aircraft ATA-100,
                Helicopter, Land Vehicle, Surface Ship or Munitions) to seed the counts.
              </P>

              <H3>2.5 BR / BREX</H3>
              <P>
                The Business Rules / BREX tab captures decision-point counts across 18 topical categories
                at the S1000D issue you select (4.0 through 6.0).
              </P>
              <UL>
                <li>Tick a category to include its DPs in the cost; untick to exclude.</li>
                <li>Override the default DP count when you have a more accurate figure.</li>
                <li>Set the "define rule" and "BREX path" minutes-per-DP to reflect your authoring speed.</li>
                <li>Use <strong>Include in project total</strong> to roll BREX cost up into the headline grand total.</li>
                <li>Generate a starter <code className="font-mono text-xs">.brex.xml</code> using the Download BREX Skeleton button — applicability is pre-seeded from the SBS systems.</li>
              </UL>
            </section>

            <section id="guide-ch3">
              <H2 id="guide-ch3">3. Planning the work</H2>

              <H3>3.1 Roles &amp; Rates</H3>
              <P>
                Edit the nine default S1000D roles or add your own. Set an hourly rate and an effort
                percentage for each role. The effort column must sum to 100&nbsp;% — the Readiness scorecard
                will warn you if it doesn't.
              </P>

              <H3>3.2 Rate Cards</H3>
              <P>
                Rate cards bundle a complete set of currency, roles, rates, default DM hours, overhead and
                contingency. They follow a lightweight approval workflow.
              </P>
              <Screenshot src="/guide-assets/ratecards.jpg" caption="the Rate Cards tab — status badges (draft / pending / approved / rejected) and per-row action buttons." />
              <H4>Lifecycle</H4>
              <UL>
                <li><strong>Draft</strong> — newly created or just edited.</li>
                <li><strong>Pending</strong> — submitted for approval; locked until approved or rejected.</li>
                <li><strong>Approved</strong> — locked. Editing pushes back to Draft for re-approval.</li>
                <li><strong>Rejected</strong> — knocked back with a reason. Edit and re-submit.</li>
              </UL>
              <H4>Common actions</H4>
              <ActionTable rows={[
                ["Apply", "Copies the rate card's rates, currency, overhead and contingency into the current estimate. Quantities are kept."],
                ["Submit", "Pushes a draft card into 'pending'. You'll be asked for your name as the submitter."],
                ["Approve", "Marks a pending card as approved. Records the approver's identity and timestamp."],
                ["Reject", "Sends the card back with a reason. The submitter can edit and re-submit."],
                ["History", "View the full version history — every save before approval is snapshotted."],
              ]} />

              <H3>3.3 Resourcing</H3>
              <P>
                Convert hours into headcount. Set the project window, pick a location preset for weekend
                days and public holidays, and configure standard and effective hours per day. The output
                shows working-day count, required FTE and per-role staffing demand.
              </P>

              <H3>3.4 Schedule</H3>
              <P>
                Spread project hours across the six standard S1000D phases (Plan, Author, Illustrate,
                Review, Validate, Publish). Edit the percent allocation, the start offset (in weeks) and
                the duration for each phase. With a start date set on Resourcing, a month-by-month grid
                shows the load profile.
              </P>
            </section>

            <section id="guide-ch4">
              <H2 id="guide-ch4">4. Preparing the bid</H2>

              <H3>4.1 Risk register</H3>
              <P>
                Log risks with category, likelihood and impact (1–5 each), mitigation and owner role. The
                score and band (LOW / MED / HIGH / CRIT) are computed for you. Turn on Auto-bump
                contingency to let aggregate risk exposure drive an uplift across the whole estimate.
              </P>

              <H3>4.2 Bid Pricing</H3>
              <P>
                Set the target margin, the win probability and any strategic adjustment. The calculator
                returns a target price, an adjusted price and an expected value. The competitor price field
                gives a side-by-side delta and a recommendation.
              </P>

              <H3>4.3 Summary</H3>
              <P>
                A read-only dashboard. Stat cards show base cost, overhead, contingency, grand total,
                blended rate, total hours and FTE. Two charts: role-effort pie and DM / illustration bar.
              </P>

              <H3>4.4 Readiness scorecard</H3>
              <P>
                The final go / no-go gate before you ship the bid. 14 checks across five dimensions return
                pass, warning or fail. The verdict is READY, WARNINGS or NOT READY with a score out of 100.
              </P>
              <Screenshot src="/guide-assets/readiness.jpg" caption="the Readiness tab — verdict card, 14-check list and the Seed Pack / Bid Pack export buttons." />
              <H4>Pack exports</H4>
              <UL>
                <li><strong>CSDB Seed Pack</strong> — ZIP with BREX skeleton, SNS XML, DMC inventory CSV, readiness scorecard, and a manifest with applicability map.</li>
                <li><strong>Bid Pack</strong> — everything in the seed pack plus risk register, schedule and bid pricing.</li>
                <li><strong>Public share links</strong> — upload either pack to the server and copy a public URL.</li>
              </UL>
              <Callout kind="tip" label="Tip">
                The seed-pack manifest is self-referential: every DMC carries an <code className="font-mono text-xs">applicPropertyValue</code> (such as
                "HYDRAULICS") that matches the BREX rule <code className="font-mono text-xs">&lt;assert&gt;</code> tokens.
              </Callout>
            </section>

            <section id="guide-ch5">
              <H2 id="guide-ch5">5. Library</H2>

              <H3>5.1 Compare</H3>
              <P>
                Pick any two saved estimates side-by-side. The Compare tab lists per-row differences and a
                grand-total delta. When currencies differ, switch on "Convert B into A's currency" to fetch
                ECB rates (cached for six hours).
              </P>
              <Screenshot src="/guide-assets/compare.jpg" caption="the Compare tab with the amber FX 1:1 fallback banner and the (1:1) marker on the Δ column header." />

              <H3>5.2 Portfolio dashboard</H3>
              <P>
                Roll-up across every saved estimate. Per-currency revenue totals, aggregated role
                utilisation, tag distribution and a top-projects table with a currency filter.
              </P>

              <H3>5.3 Saved Projects</H3>
              <P>
                Every estimate you have ever saved. Filter by name or tag, load with one click, duplicate
                to spawn a copy, or open the History modal to inspect and restore earlier revisions.
              </P>
            </section>

            <section id="guide-glossary">
              <H2 id="guide-glossary">Appendix A — Glossary</H2>
              <ActionTable rows={[
                ["SBS", "System Breakdown Structure. The product decomposed into Systems, Sub-Systems and Sub-Subsystems."],
                ["SNS", "Standard Numbering System. The S1000D scheme for system / sub-system / sub-subsystem codes."],
                ["DM", "Data Module. The atomic unit of S1000D technical content."],
                ["DMC", "Data Module Code. The unique identifier for a DM."],
                ["DML", "Data Management List. The register of every DMC, ICN and PMC in the project."],
                ["ICN", "Illustration Control Number — the identifier for an illustration."],
                ["PMC", "Publication Module Code — the identifier for a publication module."],
                ["BREX", "Business Rules Exchange. The XML data module that captures project business rules."],
                ["DP", "Decision Point. One row of authoring effort against a BR category."],
                ["FTE", "Full-Time Equivalent. Headcount expressed in standard work-days per year."],
                ["Blended rate", "The cost-weighted average hourly rate across all roles."],
                ["Seed Pack", "A ZIP bundle of starter CSDB artefacts."],
                ["Bid Pack", "A larger ZIP that adds risk, schedule and bid pricing to the seed pack."],
              ]} />
            </section>

            <section id="guide-faq">
              <H2 id="guide-faq">Appendix B — Frequently asked questions</H2>
              <H3>Why does the total cost change when I switch currency?</H3>
              <P>It doesn't. The numbers stay the same; only the currency symbol and locale-aware number formatting change. To convert to another currency, use the Compare tab's FX toggle.</P>
              <H3>Where is my data stored?</H3>
              <P>Every estimate, rate card and share link is stored in the MongoDB database that runs alongside the application. Nothing is sent to a third party — apart from a 6-hourly call to frankfurter.dev for ECB exchange rates.</P>
              <H3>Can I work on more than one estimate at the same time?</H3>
              <P>Yes. Save the first one, click <strong>New</strong> or <strong>Duplicate</strong> to clear the workspace or spawn a copy, then start the next.</P>
              <H3>How do I delete an estimate I no longer need?</H3>
              <P>Open Library → Saved Projects, locate the row, and click the bin icon. The estimate and its revision history are removed permanently.</P>
              <H3>Where do I find the latest exchange rates?</H3>
              <P>The Compare tab fetches them from the European Central Bank via frankfurter.dev and caches them for six hours. If the service is unreachable, the conversion falls back to 1.0 with a warning.</P>

              <Callout kind="note" label="Offline copy">
                Click the <strong>PDF</strong> button at the top right of this panel for a 10-page printable copy you can keep at hand.
              </Callout>
            </section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
